/**
 * @file LegalTextEditor.tsx
 * @description Текст документа вида «Текст»: редактор и предпросмотр
 * (phase19 WYSIWYG; phase19.16 — черновик новой редакции, SB143).
 *
 * Правка текста меняет только черновик: на сайте остаётся действующая
 * редакция, пока не нажата «Сохранить и опубликовать». Подсказка над
 * редактором говорит это словами, когда документ уже опубликован.
 */

import { useId, useState } from "react";

import { Segmented } from "../site-view/Segmented";
import { Label } from "../ui/label";
import { RichTextEditor } from "../ui/RichTextEditor";
import { LegalRequisitesButtons } from "./LegalRequisitesButtons";

interface Props {
  title: string;
  value: string;
  onChange: (html: string) => void;
  /** На сайте уже есть действующая редакция. */
  hasLive: boolean;
  disabled?: boolean;
}

type Mode = "edit" | "preview";

const MODE_OPTIONS = [
  { value: "edit" as const, label: "Редактор" },
  { value: "preview" as const, label: "Как на сайте" },
];

export function LegalTextEditor({ title, value, onChange, hasLive, disabled }: Props) {
  const [mode, setMode] = useState<Mode>("edit");
  const labelId = useId();
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label id={labelId}>Текст документа</Label>
        <Segmented aria-label="Режим текста" options={MODE_OPTIONS} value={mode} onChange={setMode} />
      </div>
      {hasLive ? (
        <p className="type-caption text-on-surface-variant">
          Правки сохраняются в черновик — покупатели видят действующую редакцию, пока вы не опубликуете новую.
        </p>
      ) : null}
      {mode === "preview" ? (
        <div className="rounded-xl bg-surface-lowest px-4 py-4 [box-shadow:inset_0_0_0_1px_var(--border)] sm:px-6">
          <h2 className="mb-3 text-xl font-semibold">{title || "Без названия"}</h2>
          <div
            className="prose prose-sm max-w-none prose-headings:font-semibold prose-a:text-primary"
            // Предпросмотр того, что увидит покупатель (тот же HTML, что уйдёт в редакцию).
            dangerouslySetInnerHTML={{ __html: value }}
          />
        </div>
      ) : (
        <RichTextEditor value={value} onChange={onChange} placeholder="Текст документа…" disabled={disabled} labelledBy={labelId} />
      )}
      <LegalRequisitesButtons onAppend={(html) => onChange(`${value}${html}`)} disabled={disabled || mode === "preview"} />
    </div>
  );
}
